import { Injectable } from '@angular/core';
import { AuthConfig, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { storeRouteBeforeLogin } from '../statemanagement/statemanagement.service';

const LOGIN_RETRY_KEY = 'signinRetry';
const SIGNIN_FAILED_MESSAGE = 'Sign in failed. Please try again.';

export const authCodeFlowConfig: AuthConfig = {
  issuer: environment.entraIssuer,
  clientId: environment.entraClientId,
  redirectUri: environment.oauth_redirectsignin,
  postLogoutRedirectUri: environment.oauth_redirectsignout,
  responseType: 'code',
  scope: `openid profile email api://${environment.scope}/access_as_user`,
  strictDiscoveryDocumentValidation: false,
  sessionChecksEnabled: true,
  showDebugInformation: false,
  disableAtHashCheck: true,
};

@Injectable()
export class AuthService {
  private readonly authStateSubject = new BehaviorSubject<boolean>(false);
  readonly authState$ = this.authStateSubject.asObservable();

  constructor(private readonly oauthService: OAuthService) {
    this.oauthService.configure(authCodeFlowConfig);

    this.oauthService.events
      .pipe(filter((event: OAuthEvent) => event.type === 'token_refresh_error'))
      .subscribe(() => {
        console.warn('Token refresh error detected.');
        this.authStateSubject.next(false);
        this.oauthService.logOut();
      });

    window.addEventListener('storage', (event) => {
      if (event.key === 'access_token') {
        this.authStateSubject.next(this.oauthService.hasValidAccessToken());
      }
    });
  }

  async initAuth(): Promise<void> {
    let loginSuccess = false;
    let callbackFailed = false;
    try {
      loginSuccess = await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    } catch (error) {
      // A stale or reused sign-in callback (e.g. after back/refresh) must not block the app from starting.
      console.warn('Sign-in callback could not be completed.', error);
      callbackFailed = true;
    }

    if (
      loginSuccess &&
      this.oauthService.hasValidIdToken() &&
      this.oauthService.hasValidAccessToken()
    ) {
      sessionStorage.removeItem(LOGIN_RETRY_KEY);
      this.oauthService.setupAutomaticSilentRefresh();
      this.authStateSubject.next(true);
      return;
    }

    this.authStateSubject.next(false);

    const path = window.location.pathname.toLowerCase();
    if (path.endsWith('/signout')) {
      return;
    }

    if (path.endsWith('/signin')) {
      // Drop the old code/state parameters so they are never processed again.
      window.history.replaceState(null, '', window.location.pathname);

      if (callbackFailed && sessionStorage.getItem(LOGIN_RETRY_KEY)) {
        sessionStorage.removeItem(LOGIN_RETRY_KEY);
        window.history.replaceState(null, '', `/error?message=${encodeURIComponent(SIGNIN_FAILED_MESSAGE)}`);
        return;
      }
      if (callbackFailed) {
        sessionStorage.setItem(LOGIN_RETRY_KEY, '1');
      }
    } else {
      const { pathname, search, hash } = window.location;
      storeRouteBeforeLogin(`${pathname}${search}${hash}`);
    }

    this.oauthService.initCodeFlow();

    // Keep the app from rendering while the browser redirects to the identity provider.
    return new Promise<void>(() => undefined);
  }
}
